import { Rogue } from '../Rogue'
import {
  DEFAULTS,
  IReflektionFacet,
  IReflektionFacetValue,
  IReflektionRequestPayload,
  IReflektionPayloadOptions,
  PRODUCT_FIELD_VALUES,
  widgets,
  IReflektionProcessedProduct,
  IReflektionRawProduct,
} from './types'
import type { IRogue } from '@/js/services/RogueTypes'
import { getCookie, setCookie } from '@/js/services/Utils'
import { useCustomerStore } from '@/js/stores/customerStore'

export const facetValueTextTransform: { [key: string]: (text: string) => string } = {
  // Prepend $ and format to 2 decimal places for every number found in the text, e.g. "Over 500" → "Over $500.00", "100-200" → "$100.00-$200.00"
  price      : (text: string) => text.replace(/\d+(\.\d+)?/g, n => `$${parseFloat(n).toFixed(2)}`),
  promotions : (text) => {
    const promotionVals: { [key: string]: string } = {
      made_in_usa             : 'Made in USA',
      '3_ships_free'          : '3 Ships Free',
      rogue_closeout_and_sale : 'Closeout & Sale',
      hot_deal                : 'Hot Deals',
      black_friday_dropship   : '5 for 5',
      free_shipping           : 'Free Shipping',
      rogue_hundo_enabled     : 'Hundo',
    }

    return promotionVals[text] || text
  },
  is_salable: (text: string) => (`${text}` === '0' ? 'Out of Stock' : 'In Stock'),
}

export function processPriceRangeFacet(text: string, rogue: IRogue): string {
  let value = text
  const prices = value.match(/\$((?:\d|,)*\.?\d+)/g) || []
  prices.forEach(price => value = value.replace(price, rogue.currency(price.slice(1))))

  return value
}

type FacetModifyFn = (vals: IReflektionFacetValue[], rogue: Rogue) => IReflektionFacetValue[]

// This will be checked for each facet. It lets us modify, sort etc
export const modifyFacetValues: { [key: string]: FacetModifyFn } = {
  is_salable     : (values: IReflektionFacetValue[]) => values.sort((el1, el2) => Number(el2.text) - Number(el1.text)),
  duration       : (values: IReflektionFacetValue[]) => values.sort((el1, el2) => parseInt(el1.text, 10) - parseInt(el2.text, 10)),
  days_per_week  : (values: IReflektionFacetValue[]) => values.sort((el1, el2) => parseInt(el1.text, 10) - parseInt(el2.text, 10)),
  rating_summary : () => {
    const retval: IReflektionFacetValue[] = []

    for (let stars = 5; stars > 1; stars -= 1) {
      // const val = values.find(facetVal => facetVal.text === `${stars}`)
      let str = ''

      for (let icon = 1; icon < 6; icon += 1) {
        str += `<span ${icon < stars ? 'class="star gold"' : 'class="star"'}>★</span>`
      }

      if (stars < 6) {
        str += ' & Up'
      }

      retval.push({
        count  : 0,
        id     : `${stars}`,
        label  : str,
        text   : str,
        urlKey : `${stars - 1}`,
      })
    }

    return retval
  },
  pb: (values: IReflektionFacetValue[], rogue: Rogue) => values.map(range => {
    // Since we fallback to the `text` we need to make sure we preserve the `urlKey`
    range.urlKey = range.urlKey || range.text
    range.text = processPriceRangeFacet(range.text, rogue)

    return range
  }),
}

export function processRfkFacet(key: string, facet: IReflektionFacet, rogue: Rogue): IReflektionFacet {
  facet.key = key

  let values = facet.value || []

  const transformValuesFn = modifyFacetValues[facet.key]

  if (transformValuesFn) {
    values = transformValuesFn(values, rogue)
  }

  const value: IReflektionFacetValue[] = values
    .filter(val => val.text !== 'black_friday_dropship' || rogue.featureFlags.isOn('fiveforfive_enabled'))
    .map(val => {
      let text = val.text
      val.urlKey = val.urlKey || val.text

      const textTransformFn = facetValueTextTransform[facet.key]
      if (textTransformFn) {
        text = textTransformFn(text)
      }

      return { ...val, text }
    })

  return {
    ...facet,
    display_name: normalizeFacetDisplayName(facet.display_name, rogue),
    value,
  }
}

export function normalizeFacetDisplayName(displayName: string, rogue: Rogue): string {
  let name = displayName

  if (name.toLowerCase() === 'is_salable') {
    name = 'Availability'
  }

  if (name.includes('Category')) {
    name = name.replace(/(^[\w]+).*/g, '$1')
  }

  if (name === 'Category' && rogue.router?.currentRoute?.value.path === '/iron-game-programming') {
    name = 'Brand'
  }

  return name
}

// Products in this list will display `Quote Available` if they don't have a price
export const QUOTED_PRODUCTS = ['91377']

/* Products that are created as a dummy product to show in search, but should be mapped
    an active purchasable product when displaying the PDP */
export const PLACEHOLDER_PRODUCTS: { [key: string]: { productId: number, url: string } } = {
  1119665: { productId: 1119420, url: '/rogue-color-training-2-0-zeus-custom-bumpers' }, // Zeus Custom Plates
}

export function processRfkProduct(product: IReflektionRawProduct, rogue: Rogue): IReflektionProcessedProduct {
   
  const defaultTransform = '//assets.roguefitness.com/f_auto,q_auto,b_rgb:ffffff,w_250,h_250'

  const { entity_id, sku, thumbnail = '/catalog/product_placeholder.jpg', manufacturer, cat1, cat2, cat3, price, name, url, tax_class_id, type, challenge_status } = product
  const retval: IReflektionProcessedProduct = {
    productId  : parseInt(entity_id, 10),
    thumbnail  : thumbnail.replace(defaultTransform, ''),
    price      : Number(price),
    categories : [cat1, cat2, cat3].filter(c => !!c) as string[],
    brand      : (manufacturer || [])[0],
    challenge_status,
    name,
    url,
    sku,
    type,
    attributes : {},
  }

  if (tax_class_id) {
    retval.attributes.tax_class_id = tax_class_id
  }
  if (manufacturer) {
    retval.attributes.manufacturer = manufacturer.join(',')
  }

  retval.quoted = Boolean(QUOTED_PRODUCTS.includes(entity_id) && (Number.isNaN(retval.price) || retval.price === 0))

  if (PLACEHOLDER_PRODUCTS[entity_id]) {
    retval.productId = PLACEHOLDER_PRODUCTS[entity_id].productId
    retval.url = PLACEHOLDER_PRODUCTS[entity_id].url
  }

  if (product.final_price) {
    const special = Number(product.final_price)
    if (special < retval.price) {
      retval.special_price = special
    }
  }

  if (product.rogue_rfk_custom_data) {
    try {
      const rogueRfkCustomData = Object.freeze(JSON.parse(product.rogue_rfk_custom_data)) as {
        rogue_related_label_name?: string;
        rogue_related?: {
          id: string,
          variationKey?: string,
          url?: string,
          sku: string,
          name: string,
          img: string,
          thumbnail: string,
          hover_image?: string,
          color?: string,
          plp_attribute?: string,
          price?: string,
          price_gbp?: string,
          special_price?: string,
          special_price_gbp?: string,
        }[];
        price_gbp?: string;
        special_price_gbp?: string;
        product_has_redirect?: boolean;
        default_bundle_variation_key?: string;
        hide_price?: boolean;
      }

      if (rogueRfkCustomData.rogue_related?.length) {
        const isSamePage = rogueRfkCustomData.rogue_related.every(item => Boolean(item.plp_attribute))

        retval.related = rogueRfkCustomData.rogue_related.map(item => ({
          productId     : isSamePage ? retval.productId : parseInt(item.id, 10),
          childId       : parseInt(item.id, 10),
          variationKey  : item.variationKey,
          color         : item.color,
          related       : item.img.replace(`https:${defaultTransform}`, ''),
          thumbnail     : (item.thumbnail || '/catalog/product_placeholder.jpg').replace(`https:${defaultTransform}`, ''),
          altImage      : item.hover_image?.replace(`https:${defaultTransform}`, ''),
          url           : (item.plp_attribute && item.url?.indexOf('?b=') === -1 ? `${product.url}?sku=${item.sku}` : item.url),
          name          : item.name,
          price         : Number(rogue.site.code === 'GBR' ? item.price_gbp : item.price) || undefined,
          special_price : Number(rogue.site.code === 'GBR' ? item.special_price_gbp : item.special_price) || undefined,
        })).filter(obj => obj.thumbnail !== retval.thumbnail)
      }
      retval.relatedLabelName = rogueRfkCustomData.rogue_related_label_name ? rogueRfkCustomData.rogue_related_label_name : 'colors'
      retval.variationKey = rogueRfkCustomData.default_bundle_variation_key
      retval.hidePrice = rogueRfkCustomData.hide_price

      // hasRedirect means that instead of treating this rfk product as a regular product, we're going to treat it like a
      // regular link that will likely redirect to something other than a product page.
      retval.hasRedirect = rogueRfkCustomData.product_has_redirect

      // replace reflection prices with prices from rogue custom data for GBR store
      if (rogue.site.code === 'GBR') {
        if (rogueRfkCustomData.price_gbp) {
          retval.price = Number(rogueRfkCustomData.price_gbp)
        }
        if (rogueRfkCustomData.special_price_gbp) {
          const specialGbp = Number(rogueRfkCustomData.special_price_gbp)
          if (specialGbp < retval.price) {
            retval.special_price = specialGbp
          }
        }
      }
    } catch (e) {
      console.error('Error JSON.parse-ing `rogue_rfk_custom_data`', e)
    }
  }

  retval.altImage = product.rogue_hover_image ? product.rogue_hover_image.replace(`https://${defaultTransform}`, '') : undefined

  return retval
}

export function getRfkSessionId(): string {
  if (typeof window === 'undefined') {
    return '0'
  }

  let sessionId = getCookie('rfkSID')
  if (!sessionId) {
    sessionId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
    setCookie('rfkSID', sessionId, 60)
  }

  return sessionId || '0'
}

export function encodeRfkPayload(rogue: IRogue, payload: IReflektionPayloadOptions) {
  let pageNumber = 1
  if (typeof payload.page_number === 'string') {
    pageNumber = parseInt(payload.page_number, 10)
  }

  if (payload.filter?.rating_summary) {
    const value = payload.filter?.rating_summary.value.slice(0)

    const min = Math.min(...value.map(v => parseInt(v, 10)))
    for (let i = min; i <= 5; i += 1) {
      if (!value.includes(`${i}`)) {
        value.push(`${i}`)
      }
    }
    payload.filter.rating_summary.value = value
  }

  const customerStore = useCustomerStore(rogue.pinia)

  const customerId = customerStore._id || getRfkSessionId()

  const data: IReflektionRequestPayload = {
    n_item      : typeof payload.n_item === 'number' ? payload.n_item : DEFAULTS.N_ITEM,
    page_number : pageNumber,
    content     : { product: { field: { value: PRODUCT_FIELD_VALUES } } },
    context     : {
      page : { uri: payload.uri },
      user : { user_id: customerId },
    },
    widget : { rfkid: payload.widget },
    facet  : {
      total : payload.widget !== widgets.SEARCH_PREVIEW,
      all   : true,
      max   : payload.uri?.indexOf('/shoes') || payload.uri?.indexOf('/iron-game-programming') === 0 ? -1 : 10,
    },
  }

  if (payload.filter && Object.keys(payload.filter).length) {
    data.filter = payload.filter
    if (payload.filter.promotions?.value.includes('black_friday_dropship') && !rogue.featureFlags.isOn('fiveforfive_enabled')) {
      data.filter.promotions.value = data.filter.promotions.value.filter((p: string) => p !== 'black_friday_dropship')
    }
  }

  if (payload.query) {
    data.query = { keyphrase: { value: [payload.query] } }
  }

  if (payload.sort) {
    data.sort = payload.sort
  }

  if ([widgets.SEARCH_PREVIEW, widgets.SEARCH_RESULTS].includes(payload.widget)) {
    data.suggestion = {
      trending_category : { max: 10 },
      category          : { max: 0 },
      keyphrase         : { max: 10 },
    }
  }

  return data
}
